'use client';

import { useState } from 'react';
import type { IssueLayoutBlockProps, NewsletterIssueData, NewsletterListingData, NewsletterMarketData } from '../types';
import { theme, sectionColors } from '../theme';
import { SpotlightCardBody } from './SpotlightCard';
import { EventsListBody } from './EventsList';
import { money, pickIssue, readBrowserId } from './newsletterShared';

/**
 * One issue of "The [Community] Letter" (V3.2): the editorial intro, the market tiles (numbers
 * only), just listed / just sold cards, the business spotlight, upcoming events and the month's
 * practical guide behind a download gate. CLIENT component (the gate is a form), framework-
 * agnostic. Renders from `issues` (host-injected BlockData.newsletterIssues): by `issueId`, else
 * the latest for `communitySlug`, else the first. Never fetches the issue itself.
 *
 * The gate posts to the same-origin /api/letter/guides/{id}/download (the tenant app's proxy →
 * gateway): email (+ name) → contact → `moving_guide_download` → `guide_drip`; the answer carries
 * the guide body and a PDF link, rendered inline.
 */

interface GuideResponse {
  data?: { guide?: { title: string; summary: string; body_html: string }; pdf_url?: string } | null;
  error?: string | null;
}

export function marketTiles(m: NewsletterMarketData | null | undefined): Array<{ label: string; value: string }> {
  if (!m) return [];
  const out: Array<{ label: string; value: string }> = [];
  if (m.median_sold_price != null) out.push({ label: 'Median sold price', value: money(m.median_sold_price) });
  if (m.sold_last_12 != null) out.push({ label: 'Sold, last 12 months', value: String(m.sold_last_12) });
  if (m.median_dom != null) out.push({ label: 'Median days on market', value: String(m.median_dom) });
  if (m.active != null) out.push({ label: 'For sale now', value: String(m.active) });
  if (m.months_of_supply != null) out.push({ label: 'Months of supply', value: String(m.months_of_supply) });
  if (m.median_list_price != null && out.length < 6) out.push({ label: 'Median list price', value: money(m.median_list_price) });
  return out;
}

function HomeCard({ l, sold }: { l: NewsletterListingData; sold?: boolean }) {
  return (
    <a href={l.path} className="tmpl-card group block overflow-hidden rounded-lg border border-black/10 transition hover:shadow-md">
      {l.photo ? <img src={l.photo} alt={l.address} loading="lazy" decoding="async" className="h-40 w-full object-cover" /> : <div className="h-40 w-full bg-black/5" />}
      <div className="p-3">
        <div className="text-lg font-semibold tabular-nums">{l.price != null ? money(l.price) : sold ? 'Sale price not disclosed' : 'Price on request'}</div>
        <div className="text-sm">{l.address}{l.city ? `, ${l.city}` : ''}</div>
        {l.beds != null || l.baths != null || l.sqft != null ? (
          <div className="mt-1 text-xs opacity-70">
            {[l.beds != null ? `${l.beds} bd` : null, l.baths != null ? `${l.baths} ba` : null, l.sqft != null ? `${l.sqft.toLocaleString('en-US')} sqft` : null].filter(Boolean).join(' · ')}
          </div>
        ) : null}
        {sold && l.sold_date ? <div className="mt-1 text-xs opacity-70">Closed {l.sold_date.slice(0, 10)}</div> : null}
      </div>
    </a>
  );
}

export function IssueLayoutBlock(props: IssueLayoutBlockProps & { issues?: NewsletterIssueData[] }) {
  const { heading, subheading, buttonText = 'Send me the guide', issues = [] } = props;
  const issue = pickIssue(issues, { issueId: props.issueId || null, communitySlug: props.communitySlug || null });
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unlocked, setUnlocked] = useState<{ title: string; summary: string; body_html: string; pdf_url: string | null } | null>(null);

  if (!issue) {
    return (
      <section style={{ ...colors, fontFamily: theme.bodyFont }} className="px-6 py-12 text-center" data-block="issue-layout">
        <p className="opacity-60">{heading ? `${heading}: ` : ''}The first issue is on its way.</p>
      </section>
    );
  }

  const s = issue.sections;
  const tiles = props.hideMarket ? [] : marketTiles(s.market);
  const h2 = 'text-2xl font-semibold';
  const cta = s.cta === 'valuation'
    ? { line: `Thinking about a move? See what your ${s.community.name} home is worth today — built from real nearby sales.`, button: "What's my home worth?" }
    : { line: `Looking at ${s.community.name}? Get matched to the right community, or an alert the moment a home that fits comes up.`, button: 'Find my fit' };

  const unlock = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    if (!issue.guide) return;
    setError(null);
    setPending(true);
    try {
      const res = await fetch(`/api/letter/guides/${encodeURIComponent(issue.id)}/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() || undefined, email: email.trim(), browser_id: readBrowserId(), page_url: typeof window !== 'undefined' ? window.location.href : undefined }),
      });
      const body = (await res.json().catch(() => ({}))) as GuideResponse;
      if (!res.ok || !body.data?.guide) throw new Error(body.error || `Request failed: ${res.status}`);
      setUnlocked({ ...body.data.guide, pdf_url: body.data.pdf_url ?? null });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong — try again.');
    } finally {
      setPending(false);
    }
  };

  return (
    <section style={{ ...colors, fontFamily: theme.bodyFont }} className="px-6 py-12" data-block="issue-layout" data-issue-id={issue.id}>
      <div className="mx-auto max-w-3xl space-y-12">
        {heading ? <h2 className={h2} style={{ fontFamily: theme.headingFont }}>{heading}</h2> : null}
        {subheading ? <p className="opacity-80">{subheading}</p> : null}

        {issue.cover_image_url ? (
          <figure>
            <img src={issue.cover_image_url} alt={issue.title} className="h-64 w-full rounded-xl object-cover" loading="lazy" decoding="async" />
            {issue.cover_credit ? <figcaption className="mt-1 text-xs opacity-60">{issue.cover_credit}</figcaption> : null}
          </figure>
        ) : null}

        {s.intro.length ? (
          <div className="space-y-4 text-lg leading-relaxed" data-testid="issue-intro">
            {s.intro.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        ) : null}

        {tiles.length ? (
          <div data-testid="issue-market">
            <h2 className={h2} style={{ fontFamily: theme.headingFont }}>This month&rsquo;s market in {s.community.name}</h2>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {tiles.map((t) => (
                <div key={t.label} className="rounded-xl border border-black/10 p-4" style={{ backgroundColor: theme.surface }}>
                  <div className="text-2xl font-semibold tabular-nums">{t.value}</div>
                  <div className="mt-1 text-xs uppercase tracking-wide opacity-70">{t.label}</div>
                </div>
              ))}
            </div>
            {s.market?.as_of ? <p className="mt-2 text-xs opacity-60">Numbers from the local MLS feed, as of {s.market.as_of.slice(0, 10)}.</p> : null}
          </div>
        ) : null}

        {!props.hideListings && s.just_listed.length ? (
          <div data-testid="issue-listed">
            <h2 className={h2} style={{ fontFamily: theme.headingFont }}>Just listed</h2>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">{s.just_listed.map((l) => <HomeCard key={l.key} l={l} />)}</div>
          </div>
        ) : null}

        {!props.hideListings && s.just_sold.length ? (
          <div data-testid="issue-sold">
            <h2 className={h2} style={{ fontFamily: theme.headingFont }}>Just sold</h2>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">{s.just_sold.map((l) => <HomeCard key={l.key} l={l} sold />)}</div>
          </div>
        ) : null}

        {!props.hideSpotlight && s.spotlight ? (
          <div data-testid="issue-spotlight">
            <h2 className={h2} style={{ fontFamily: theme.headingFont }}>Local business spotlight</h2>
            <div className="mt-4"><SpotlightCardBody spotlight={s.spotlight} /></div>
          </div>
        ) : null}

        {!props.hideEvents && s.events.length ? (
          <div data-testid="issue-events">
            <h2 className={h2} style={{ fontFamily: theme.headingFont }}>Coming up</h2>
            <div className="mt-4"><EventsListBody events={s.events} /></div>
          </div>
        ) : null}

        {!props.hideGuide && issue.guide ? (
          <div id="guide" className="rounded-2xl border p-6 sm:p-8" style={{ borderColor: theme.primary, backgroundColor: theme.surface }} data-testid="issue-guide">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] opacity-70">This month&rsquo;s guide</p>
            <h2 className={`${h2} mt-2`} style={{ fontFamily: theme.headingFont }}>{issue.guide.title}</h2>
            {issue.guide.summary ? <p className="mt-2 opacity-80">{issue.guide.summary}</p> : null}
            {unlocked ? (
              <div className="mt-6" data-testid="guide-unlocked">
                {unlocked.pdf_url ? (
                  <a href={unlocked.pdf_url} target="_blank" rel="noopener" className="blk-btn inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold">Open the PDF</a>
                ) : null}
                <div className="prose mt-6 max-w-none" dangerouslySetInnerHTML={{ __html: unlocked.body_html }} />
              </div>
            ) : (
              <form onSubmit={unlock} className="mt-6 grid gap-3 sm:grid-cols-[1fr_1fr_auto]" data-testid="guide-gate">
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" aria-label="Your name" className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-gray-900" />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" aria-label="Email address" className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-gray-900" />
                <button type="submit" disabled={pending} className="blk-btn rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-60">
                  {pending ? 'Sending…' : buttonText}
                </button>
                {error ? <p className="text-sm text-red-700 sm:col-span-3" role="alert">{error}</p> : null}
                <p className="text-xs opacity-60 sm:col-span-3">The guide opens here and a PDF link goes to your email. Unsubscribe any time.</p>
              </form>
            )}
          </div>
        ) : null}

        <div className="rounded-2xl p-6 text-center sm:p-8" style={{ backgroundColor: theme.secondary, color: theme.onSecondary }} data-testid="issue-cta">
          <p className="text-lg">{cta.line}</p>
          <a href={(props.ctaHref ?? '').trim() || (s.cta === 'valuation' ? '/contact' : `/communities/${encodeURIComponent(s.community.slug)}`)} data-cta={s.cta} className="blk-btn mt-4 inline-flex items-center rounded-lg px-5 py-2.5 text-sm font-semibold">
            {cta.button}
          </a>
        </div>

      </div>
    </section>
  );
}
